/**
 * Brand Context — Phase 3.0 (Dual-Brand Architecture)
 * ====================================================
 *
 * Single source of truth for "which brand is this user looking at?"
 *
 * How it works
 * ------------
 * Two top-level domains share one backend + database, but render two
 * distinct consumer brands:
 *
 *   homesbyli.com           → STAYS brand   (existing — short-term rentals)
 *   ucwonder.com            → UCWONDER      (parent brand — AI travel concierge
 *                                             + experiences + gift cards + stays)
 *
 * The brand is resolved from (in order of precedence):
 *   1. URL query param `?brand=marketplace` or `?brand=stays`
 *      — dev/preview override so we can test both brands without DNS work
 *   2. localStorage `brand_override` — sticky preview override
 *   3. `window.location.hostname` match against the brand registry
 *   4. Fallback: STAYS (Homesbyli — current production behaviour)
 *
 * Everything UI-visible (logo, copy, palette accents, header nav,
 * footer links, CTA labels) flows from the BRAND_REGISTRY below.
 *
 * Parent brand — UCWonder
 * -----------------------
 * "UCWonder" reads phonetically as "you see wonder" — the brand is an
 * AI travel concierge, so all copy leads with the concierge / AI-powered
 * discovery angle. Gift cards + experiences + stays are pillars UNDER
 * that hero narrative, not the hero itself.
 */

// ── Palette (locked, shared across brands) ─────────────────────────
const PALETTE = {
  navy:    '#1E3F70',
  pine:    '#1F5C42',
  pink:    '#FF5C8A',
  offwhite: '#F8F5EE',
  gold:    '#FFC93C',
};

// ── Brand registry ─────────────────────────────────────────────────
// Two brand contexts. Add more here later (e.g. white-label / partner
// brands) by appending entries — every other module reads from this map.
export const BRAND_REGISTRY = {
  stays: {
    key: 'stays',
    // Display
    displayName: 'Homesbyli',
    legalName: 'Homesbyli Tech Inc.',
    tagline: 'Find your Montreal home',
    descriptor: 'Short-term rentals & local stays in Montreal.',
    // Identity
    hostnames: ['homesbyli.com', 'www.homesbyli.com'],
    supportEmail: 'support@homesbyli.com',
    bookingEmail: 'booking@homesbyli.com',
    primaryColor: PALETTE.pine,
    accentColor: PALETTE.pink,
    // Navigation — header CTA strip + footer
    primaryNav: [
      { label: 'Search homes',    to: '/search'      },
      { label: 'AI concierge',    to: '/plan',   cross: 'marketplace' },
      { label: 'Experiences',     to: '/experiences', cross: 'marketplace' },
      { label: 'Gift cards',      to: '/dining',      cross: 'marketplace' },
      { label: 'Become a host',   to: '/host-application' },
    ],
    // Landing page — which route renders at `/`
    landingRoute: '/',          // current WelcomePage
    landingComponentKey: 'WelcomePage',
    // How this brand introduces the OTHER brand in footers + cross-sells
    crossLinkLabel: 'Looking for an AI travel concierge, gift cards or experiences?',
    crossLinkCta: 'Explore UCWonder →',
    // Subtle "part of parent" footer line — appears on stays domain only
    partOfLine: 'Part of UCWonder',
    // SEO — per-domain <title>/meta/OG (applied at runtime by <BrandSEO/>)
    seo: {
      title: 'Homesbyli — Short-Term Rentals in Montreal',
      description:
        'Find the perfect short-term rental in Montreal. Quality apartments, ' +
        'amazing experiences, and authentic Montreal living.',
      canonicalOrigin: 'https://homesbyli.com',
      favicon: '/favicon.svg',
      ogImage: 'https://homesbyli.com/og-image.jpg',
      themeColor: PALETTE.pine,
    },
  },

  marketplace: {
    key: 'marketplace',
    // Display — UCWonder ("you see wonder"): AI travel concierge that
    // unifies stays, experiences, and gift cards. AI concierge leads
    // every consumer surface; gift cards are a supporting pillar.
    displayName: 'UCWonder',
    legalName: 'UCWonder Inc.',
    tagline: 'You see wonder',
    descriptor:
      'An AI travel concierge. Plan smarter trips, unlock curated ' +
      'stays, hand-picked local experiences, and instant gift cards — ' +
      'everything in one intelligent place.',
    // Identity
    hostnames: ['ucwonder.com', 'www.ucwonder.com'],
    supportEmail: 'support@ucwonder.com',
    bookingEmail: 'orders@ucwonder.com',
    primaryColor: PALETTE.navy,
    accentColor: PALETTE.gold,
    // Navigation — CONCIERGE LEADS. Gift cards drops to the tail because
    // it's a supporting pillar, not the hero (per 2026-07-02 reposition).
    primaryNav: [
      { label: 'AI concierge', to: '/plan' },
      { label: 'Experiences',  to: '/experiences' },
      { label: 'Stays',        to: '/search', cross: 'stays' },
      { label: 'Gift cards',   to: '/dining' },
    ],
    landingRoute: '/',
    landingComponentKey: 'LandingMarketplace',
    crossLinkLabel: 'Looking for a Montreal home?',
    crossLinkCta: 'Browse Homesbyli stays →',
    // Parent brand — no "part of" line (this IS the parent)
    partOfLine: null,
    // SEO — per-domain <title>/meta/OG (applied at runtime by <BrandSEO/>)
    seo: {
      title: 'UCWonder — Your AI Travel Concierge',
      description:
        'Plan smarter trips with an AI concierge: curated Montreal stays, ' +
        'hand-picked experiences, and instant gift cards — all in one place.',
      canonicalOrigin: 'https://ucwonder.com',
      favicon: '/ucwonder-favicon.png',
      ogImage: 'https://ucwonder.com/ucwonder-og.png',
      themeColor: PALETTE.navy,
    },
  },
};

export const PALETTE_TOKENS = PALETTE;


// ── Resolver ───────────────────────────────────────────────────────
const DEFAULT_BRAND = 'stays';

/**
 * Resolve the active brand key from the current environment.
 * Pure function — easy to test, easy to mock.
 *
 * @param {Object} [opts]
 * @param {string} [opts.hostname]   - override window.location.hostname
 * @param {string} [opts.search]     - override window.location.search
 * @param {Storage} [opts.storage]   - override localStorage
 * @returns {'stays'|'marketplace'}
 */
export function resolveBrandKey(opts = {}) {
  const hostname = opts.hostname
    ?? (typeof window !== 'undefined' ? window.location.hostname : '');
  const search = opts.search
    ?? (typeof window !== 'undefined' ? window.location.search : '');
  const storage = opts.storage
    ?? (typeof window !== 'undefined' ? window.localStorage : null);

  // 1. ?brand=… query param — highest precedence (dev/preview override)
  try {
    const params = new URLSearchParams(search);
    const fromQuery = params.get('brand');
    if (fromQuery && BRAND_REGISTRY[fromQuery]) {
      // Persist the choice so subsequent navigations stay on this brand
      // until the user explicitly opts out.
      try { storage?.setItem('brand_override', fromQuery); } catch { /* private mode */ }
      return fromQuery;
    }
  } catch { /* ignore malformed search strings */ }

  // 2. Sticky localStorage override (set by query param above)
  try {
    const fromStorage = storage?.getItem('brand_override');
    if (fromStorage && BRAND_REGISTRY[fromStorage]) return fromStorage;
  } catch { /* private mode */ }

  // 3. Hostname match
  for (const key of Object.keys(BRAND_REGISTRY)) {
    const hosts = BRAND_REGISTRY[key].hostnames || [];
    if (hosts.some((h) => h === hostname)) return key;
  }

  // 4. Fallback — current production behaviour for unknown hostnames
  // (preview URL, localhost, custom domains we haven't registered yet).
  return DEFAULT_BRAND;
}

/**
 * Get the resolved brand config (full object, not just the key).
 */
export function resolveBrand(opts = {}) {
  return BRAND_REGISTRY[resolveBrandKey(opts)] || BRAND_REGISTRY[DEFAULT_BRAND];
}

/**
 * Get the OTHER brand (used for cross-brand footer/header links).
 * Returns null if there's no sibling brand.
 */
export function siblingBrand(currentKey) {
  const other = Object.keys(BRAND_REGISTRY).find((k) => k !== currentKey);
  return other ? BRAND_REGISTRY[other] : null;
}

/**
 * Clear the sticky preview override (e.g. from a developer settings panel).
 */
export function clearBrandOverride(storage) {
  try {
    const s = storage ?? (typeof window !== 'undefined' ? window.localStorage : null);
    s?.removeItem('brand_override');
  } catch { /* ignore */ }
}
